/**
 * US-150: Resume Upload API Methods
 */

const BASE_URL = import.meta.env.VITE_API_URL || '';

export interface ResumeUpdateResponse {
  candidateId: number;
  applicationId: number;
  resumeText: string;
  resumeSource: string;
  characterCount: number;
  fileName: string | null;
  mimeType: string | null;
  fileSize: number | null;
  pageCount: number | null;
  updatedAt: string;
}

/** Paste resume text for an application's candidate */
export async function updateApplicationResume(
  applicationId: number,
  resumeText: string
): Promise<ResumeUpdateResponse> {
  const response = await fetch(`${BASE_URL}/api/applications/${applicationId}/resume`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resumeText }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: 'Failed to save resume' }));
    throw new Error(typeof data.error === 'string' ? data.error : 'Failed to save resume');
  }

  return response.json();
}

/** Upload a file (PDF/DOCX/TXT) for an application's candidate */
export async function uploadApplicationResume(
  applicationId: number,
  file: File
): Promise<ResumeUpdateResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${BASE_URL}/api/applications/${applicationId}/resume`, {
    method: 'PATCH',
    credentials: 'include',
    // Don't set Content-Type — browser sets multipart boundary automatically
    body: formData,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: 'Failed to upload resume' }));
    throw new Error(typeof data.error === 'string' ? data.error : 'Failed to upload resume');
  }

  return response.json();
}
